import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import { tokens } from '@companycam/slab';

import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';

import SelectableTemplateRow from './SelectableTemplateRow';
import TemplateEmptyState from './TemplateEmptyState';
import { Button, Spinner } from '@companycam/slab-web';
import { translate } from 'translations/i18n';
import { useBackwardsCompatibleNavigate } from 'components/navigation/useBackwardsCompatibleNavigation';

const StyledCCModal = styled(CCModal)`
  height: 75vh;
  padding: ${tokens.size_spacing_s} 0;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: -24px calc(var(--cc_size_spacing_xl) * -1)
    calc(var(--cc_size_spacing_xl) * -1);
`;

const MultiSelectModal = ({
  isOpen,
  title,
  templates,
  hasNextPage,
  fetchMore,
  onRequestClose,
  onAddTemplates,
  emptyState,
  loading,
  footerContentBefore,
  primaryActionLabel = null,
}) => {
  const navigate = useBackwardsCompatibleNavigate();
  const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);

  const onClearSelection = () => {
    setSelectedTemplateIds([]);
  };

  useEffect(() => {
    if (!isOpen) {
      onClearSelection();
    }
  }, [isOpen]);

  const [ref, inView] = useInView({
    threshold: 0,
    rootMargin: '0px 0px 300px 0px',
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchMore();
    }
  }, [inView, hasNextPage, fetchMore]);

  const renderTemplates = () => {
    if (loading) {
      return <Spinner accessibilityLabel={translate('common.loading')} />;
    }

    if (!templates.length) {
      return emptyState;
    }

    const onSelect = (id) => {
      if (!selectedTemplateIds.includes(id)) {
        setSelectedTemplateIds([...selectedTemplateIds, id]);
      } else {
        setSelectedTemplateIds(
          selectedTemplateIds.filter((templateId) => {
            return templateId !== id;
          }),
        );
      }
    };

    if (templates.length === 0) {
      if (emptyState) {
        return emptyState;
      }

      return (
        <TemplateEmptyState
          message={translate('templates.shared.multiSelectModal.empty')}
          submessage={translate('template.shared.defaultEmptyStateMsg')}
          action={{
            label: translate('template.shared.goToTemplates'),
            hideIcon: true,
            onClick: () => navigate('/templates'),
          }}
        />
      );
    }

    return templates.map((template) => {
      return (
        <SelectableTemplateRow
          key={template.id}
          id={template.id}
          title={template.name}
          description={template.description}
          isSelected={selectedTemplateIds.includes(template.id)}
          onSelect={onSelect}
        />
      );
    });
  };

  return (
    <StyledCCModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size="medium"
      title={title}
      primaryAction={{
        label: primaryActionLabel ?? translate('template.shared.addTemplates'),
        onClick: () => onAddTemplates(selectedTemplateIds),
        disabled: selectedTemplateIds.length === 0 || loading,
      }}
      secondaryAction={{
        label: translate('template.shared.clearSelection'),
        onClick: onClearSelection,
        disabled: loading,
      }}
      footerContentBefore={
        footerContentBefore ? (
          <Button
            color="secondary"
            design="minimal"
            onClick={footerContentBefore.onClick}
          >
            {footerContentBefore.label}
          </Button>
        ) : null
      }
    >
      <ModalBody>
        {renderTemplates()}
        <div ref={ref} style={{ height: '1px', opacity: 0 }} />
      </ModalBody>
    </StyledCCModal>
  );
};

MultiSelectModal.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  hasNextPage: PropTypes.bool.isRequired,
  fetchMore: PropTypes.func.isRequired,
  onAddTemplates: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
    }),
  ),
  emptyState: PropTypes.element,
  loading: PropTypes.bool,
  footerContentBefore: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }),
  primaryActionLabel: PropTypes.string,
};

export default MultiSelectModal;
